import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import FormField from "@cloudscape-design/components/form-field"
import Input from "@cloudscape-design/components/input"
import Modal from "@cloudscape-design/components/modal"
import RadioGroup from "@cloudscape-design/components/radio-group"
import Select, { type SelectProps } from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Table, { type TableProps } from "@cloudscape-design/components/table"
import { useState, useRef } from "react"
import type { CodelistItem, CodelistValue } from "@/generated/sdk"

interface CodelistImportWizardProps {
  isOpen: boolean
  onClose: () => void
  onImport: (items: CodelistItem[]) => void
  existingItems: CodelistItem[]
}

interface ParsedCodelist {
  name: string
  values: CodelistValue[]
  isDuplicate: boolean
  selected: boolean
  overwrite: boolean
}

type Step = "mapping" | "preview"

const CODE_PATTERNS = ["code", "coded", "test code", "test codes"]
const DECODE_PATTERNS = ["decode", "decoded", "test name", "test names"]
const GROUPING_PATTERNS = ["codelist name", "category", "data type", "name"]

export function recommend(headers: string[], patterns: string[]): string {
  const lower = headers.map((h) => h.toLowerCase().trim())
  for (const p of patterns) {
    const idx = lower.indexOf(p)
    if (idx !== -1) return headers[idx]
  }
  return ""
}

export function recommendCodeCol(headers: string[], rows: string[][], groupingCol: string): string {
  const groupIdx = groupingCol ? headers.indexOf(groupingCol) : -1

  function hasUniqueValues(colIdx: number): boolean {
    const groups = new Map<string, Set<string>>()
    for (const row of rows) {
      const group = groupIdx !== -1 ? row[groupIdx] || "" : "_"
      const val = row[colIdx] || ""
      if (!val) continue
      if (!groups.has(group)) groups.set(group, new Set())
      const seen = groups.get(group)!
      if (seen.has(val)) return false
      seen.add(val)
    }
    return true
  }

  const lower = headers.map((h) => h.toLowerCase().trim())
  for (const p of CODE_PATTERNS) {
    const idx = lower.indexOf(p)
    if (idx !== -1 && hasUniqueValues(idx)) return headers[idx]
  }
  // Fallback: first column with unique values per group
  for (let i = 0; i < headers.length; i++) {
    if (i === groupIdx) continue
    if (hasUniqueValues(i)) return headers[i]
  }
  return headers[0] || ""
}

export function parseCSVText(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim())
  if (lines.length < 2) return { headers: [], rows: [] }
  const delimiter = lines[0].includes("\t") ? "\t" : ","
  const headers = lines[0].split(delimiter).map((h) => h.trim().replace(/^["']|["']$/g, ""))
  const rows: string[][] = []
  for (let i = 1; i < lines.length; i++) {
    rows.push(lines[i].split(delimiter).map((c) => c.trim().replace(/^["']|["']$/g, "")))
  }
  return { headers, rows }
}

export function extractCodelists(
  rows: string[][],
  headers: string[],
  groupingCol: string,
  codeCol: string,
  decodeCol: string,
  otherCol: string,
  fallbackName: string,
  existingNames: Set<string>,
): ParsedCodelist[] {
  const codeIdx = headers.indexOf(codeCol)
  const decodeIdx = decodeCol ? headers.indexOf(decodeCol) : -1
  const otherIdx = otherCol ? headers.indexOf(otherCol) : -1
  const groupIdx = groupingCol ? headers.indexOf(groupingCol) : -1

  if (codeIdx === -1) return []

  const groups = new Map<string, CodelistValue[]>()
  const seen = new Map<string, Set<string>>()
  for (const row of rows) {
    const code = (row[codeIdx] || "").slice(0, 255)
    if (!code) continue
    const decode = decodeIdx !== -1 ? (row[decodeIdx] || "").slice(0, 255) : ""
    const other = otherIdx !== -1 ? (row[otherIdx] || "").slice(0, 255) : ""
    const group = groupIdx !== -1 ? (row[groupIdx] || "").trim() : fallbackName
    if (!group) continue
    if (!groups.has(group)) {
      groups.set(group, [])
      seen.set(group, new Set())
    }
    const groupSeen = seen.get(group)!
    if (groupSeen.has(code)) continue
    groupSeen.add(code)
    const vals = groups.get(group)!
    if (vals.length < 500) vals.push({ code, decode, other_value: other })
  }

  return Array.from(groups.entries()).map(([name, values]) => ({
    name,
    values,
    isDuplicate: existingNames.has(name.toLowerCase()),
    selected: true,
    overwrite: true,
  }))
}

export const CodelistImportWizard: React.FC<CodelistImportWizardProps> = ({
  isOpen,
  onClose,
  onImport,
  existingItems,
}) => {
  const [step, setStep] = useState<Step>("mapping")
  const [fileName, setFileName] = useState("")
  const [headers, setHeaders] = useState<string[]>([])
  const [rows, setRows] = useState<string[][]>([])
  const [groupingCol, setGroupingCol] = useState("")
  const [codeCol, setCodeCol] = useState("")
  const [decodeCol, setDecodeCol] = useState("")
  const [otherCol, setOtherCol] = useState("")
  const [fallbackName, setFallbackName] = useState("")
  const [codelists, setCodelists] = useState<ParsedCodelist[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const reset = () => {
    setStep("mapping")
    setFileName("")
    setHeaders([])
    setRows([])
    setGroupingCol("")
    setCodeCol("")
    setDecodeCol("")
    setOtherCol("")
    setFallbackName("")
    setCodelists([])
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ""
    const reader = new FileReader()
    reader.onload = () => {
      const { headers: h, rows: r } = parseCSVText(reader.result as string)
      if (h.length === 0) {
        alert("Could not parse CSV. Ensure it has a header row and at least one data row.")
        return
      }
      setFileName(file.name)
      setHeaders(h)
      setRows(r)
      const grouping = recommend(h, GROUPING_PATTERNS)
      setGroupingCol(grouping)
      setCodeCol(recommendCodeCol(h, r, grouping))
      setDecodeCol(recommend(h, DECODE_PATTERNS))
      setOtherCol(h.find((col) => col.toLowerCase().includes("other")) || "")
      setFallbackName(file.name.replace(/\.(csv|tsv|txt)$/i, ""))
    }
    reader.readAsText(file)
  }

  const handlePreview = () => {
    if (!codeCol) return
    const existingNames = new Set(existingItems.map((i) => i.name.toLowerCase()))
    const result = extractCodelists(
      rows,
      headers,
      groupingCol,
      codeCol,
      decodeCol,
      otherCol,
      fallbackName,
      existingNames,
    )
    if (result.length === 0) {
      alert("No codelists could be extracted. Check your column mapping.")
      return
    }
    setCodelists(result)
    setStep("preview")
  }

  const handleImport = () => {
    const selected = codelists.filter((cl) => cl.selected)
    const items: CodelistItem[] = selected
      .filter((cl) => !cl.isDuplicate || cl.overwrite)
      .map((cl) => ({
        id: crypto.randomUUID(),
        name: cl.name,
        column: "(assign column)",
        extensible: false,
        severity: "major" as const,
        source: "csv_import",
        source_id: undefined,
        conditions: [],
        values: cl.values,
      }))
    onImport(items)
    handleClose()
  }

  if (!isOpen) return null

  // Column-mapping selects. Grouping/decode/other allow an explicit "none" ("") option;
  // the code column is required, so an unset value shows the placeholder instead.
  const headerOptions: SelectProps.Option[] = headers.map((h) => ({ value: h, label: h }))
  const groupingOptions: SelectProps.Option[] = [
    { value: "", label: "-- None (single codelist) --" },
    ...headerOptions,
  ]
  const optionalOptions: SelectProps.Option[] = [
    { value: "", label: "-- None --" },
    ...headerOptions,
  ]

  const selectedFor = (options: SelectProps.Option[], value: string) =>
    options.find((o) => o.value === value) ?? null

  const previewItems = codelists.map((cl, i) => ({ cl, i }))

  const importCount = codelists.filter((c) => c.selected && (!c.isDuplicate || c.overwrite)).length

  const previewColumns: TableProps.ColumnDefinition<{ cl: ParsedCodelist; i: number }>[] = [
    {
      id: "name",
      header: "Codelist Name",
      cell: ({ cl }) => <Box variant="strong">{cl.name}</Box>,
    },
    {
      id: "values",
      header: "Values",
      cell: ({ cl }) => (
        <span>
          {cl.values.length}
          {cl.isDuplicate && (
            <Box variant="span" color="text-body-secondary" fontSize="body-s">
              {" "}
              (existing:{" "}
              {existingItems.find((e) => e.name.toLowerCase() === cl.name.toLowerCase())?.values
                .length ?? 0}
              )
            </Box>
          )}
        </span>
      ),
    },
    {
      id: "action",
      header: "Action",
      cell: ({ cl, i }) =>
        cl.isDuplicate ? (
          <RadioGroup
            value={cl.overwrite ? "overwrite" : "keep"}
            onChange={({ detail }) =>
              setCodelists((prev) =>
                prev.map((c, j) =>
                  j === i ? { ...c, selected: true, overwrite: detail.value === "overwrite" } : c,
                ),
              )
            }
            items={[
              { value: "overwrite", label: "Overwrite" },
              { value: "keep", label: "Keep existing" },
            ]}
          />
        ) : (
          <RadioGroup
            value={cl.selected ? "import" : "ignore"}
            onChange={({ detail }) =>
              setCodelists((prev) =>
                prev.map((c, j) =>
                  j === i ? { ...c, selected: detail.value === "import", overwrite: true } : c,
                ),
              )
            }
            items={[
              { value: "import", label: "Import" },
              { value: "ignore", label: "Ignore" },
            ]}
          />
        ),
    },
  ]

  // Data-preview table: one column per header, first 5 rows. A Badge on the header
  // conveys which columns are mapped (replacing the old color-coded highlight).
  const previewDataColumns: TableProps.ColumnDefinition<{ row: string[]; ri: number }>[] =
    headers.map((h, ci) => {
      const role =
        h === codeCol
          ? "code"
          : h === decodeCol
            ? "decode"
            : h === groupingCol
              ? "name"
              : h === otherCol
                ? "other"
                : null
      return {
        id: `col-${ci}`,
        header: (
          <span>
            {h}
            {role && (
              <>
                {" "}
                <Badge color="blue">{role}</Badge>
              </>
            )}
          </span>
        ),
        cell: ({ row }) => row[ci] || "—",
      }
    })

  const columnSelect = (
    label: React.ReactNode,
    options: SelectProps.Option[],
    value: string,
    onChange: (value: string) => void,
    ariaLabel: string,
    placeholder?: string,
  ) => (
    <FormField label={label}>
      <Select
        selectedOption={
          placeholder ? selectedFor(headerOptions, value) : selectedFor(options, value)
        }
        onChange={({ detail }) => onChange(detail.selectedOption.value ?? "")}
        options={placeholder ? headerOptions : options}
        placeholder={placeholder}
        ariaLabel={ariaLabel}
      />
    </FormField>
  )

  const mappingContent = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.tsv,.txt"
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />
      {!fileName ? (
        <SpaceBetween size="s" direction="horizontal" alignItems="center">
          <Box color="text-body-secondary">No file selected</Box>
          <Button iconName="upload" onClick={() => fileInputRef.current?.click()}>
            Select file
          </Button>
        </SpaceBetween>
      ) : (
        <SpaceBetween size="l">
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Box variant="strong">{fileName}</Box>
            <Button iconName="refresh" onClick={() => fileInputRef.current?.click()}>
              Change file
            </Button>
            <Box color="text-body-secondary" fontSize="body-s">
              {headers.length} columns, {rows.length} rows
            </Box>
          </SpaceBetween>

          <SpaceBetween size="m">
            {columnSelect(
              "Codelist Name column",
              groupingOptions,
              groupingCol,
              setGroupingCol,
              "Codelist Name column",
            )}

            {!groupingCol && (
              <FormField label="Codelist name">
                <Input
                  value={fallbackName}
                  onChange={({ detail }) => setFallbackName(detail.value)}
                  placeholder="Name for this codelist"
                />
              </FormField>
            )}

            {columnSelect(
              <>
                Code column{" "}
                <Box variant="span" color="text-status-error">
                  *
                </Box>
              </>,
              headerOptions,
              codeCol,
              setCodeCol,
              "Code column",
              "-- Select --",
            )}

            {columnSelect(
              "Decode column (optional)",
              optionalOptions,
              decodeCol,
              setDecodeCol,
              "Decode column",
            )}

            {columnSelect(
              "Other Value column (optional)",
              optionalOptions,
              otherCol,
              setOtherCol,
              "Other Value column",
            )}
          </SpaceBetween>

          {rows.length > 0 && (
            <div>
              <Box variant="awsui-key-label" margin={{ bottom: "xs" }}>
                Data Preview{" "}
                <Box variant="span" color="text-body-secondary" fontSize="body-s">
                  ({Math.min(rows.length, 5)} of {rows.length} rows)
                </Box>
              </Box>
              <Table
                variant="embedded"
                columnDefinitions={previewDataColumns}
                items={rows.slice(0, 5).map((row, ri) => ({ row, ri }))}
                trackBy={(item) => String(item.ri)}
              />
            </div>
          )}
        </SpaceBetween>
      )}
    </>
  )

  const previewContent = (
    <SpaceBetween size="l">
      <Box color="text-body-secondary">
        {codelists.length} codelist{codelists.length !== 1 && "s"} found. Select which to import.
      </Box>
      <Table
        variant="embedded"
        columnDefinitions={previewColumns}
        items={previewItems}
        trackBy={(item) => item.cl.name}
      />
    </SpaceBetween>
  )

  const footer =
    step === "mapping" ? (
      <Box float="right">
        <SpaceBetween size="xs" direction="horizontal">
          <Button variant="link" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handlePreview} disabled={!codeCol}>
            Preview
          </Button>
        </SpaceBetween>
      </Box>
    ) : (
      <Box float="right">
        <SpaceBetween size="xs" direction="horizontal">
          <Button variant="link" onClick={() => setStep("mapping")}>
            Back
          </Button>
          <Button
            variant="primary"
            onClick={handleImport}
            disabled={!codelists.some((c) => c.selected)}
          >
            Import ({importCount})
          </Button>
        </SpaceBetween>
      </Box>
    )

  return (
    <Modal
      visible
      onDismiss={handleClose}
      closeAriaLabel="Close"
      header="Import from CSV"
      size="max"
      footer={footer}
    >
      {step === "mapping" ? mappingContent : previewContent}
    </Modal>
  )
}
